// ============================================================
// DragSystem — click-to-place and drag-to-move for spawned objects
//
// Two modes:
//  1. PLACEMENT MODE  — after typing a word, a ghost follows the
//     cursor; left-click places the object there exactly.
//  2. DRAG MODE       — click-and-hold any existing spawned object
//     to pick it up and reposition it; release to drop.
// ============================================================
import * as PIXI from 'pixi.js';
import { Z } from '../constants.js';

export class DragSystem {
  /**
   * @param {PIXI.Application} app
   * @param {PIXI.Container}   worldContainer  - scrolling world layer
   * @param {HTMLCanvasElement} canvas
   */
  constructor(app, worldContainer, canvas) {
    this.app            = app;
    this.worldContainer = worldContainer;
    this.canvas         = canvas;

    // Placement mode state
    this._placingConfig = null;
    this._ghost         = null;

    // Drag mode state
    this._dragging      = null;
    this._dragOffX      = 0;
    this._dragOffY      = 0;
    this._prevStatic    = false;

    // Mouse world position
    this._mx = 0;
    this._my = 0;

    // Callbacks set by Game
    this.onPlace    = null;   // fn(config, worldX, worldY)
    this.onCancel   = null;
    this.onDelete   = null;   // fn(entity) — called when dropped on trash

    // Entities list reference (set by Game)
    this.entities = [];

    // HUD reference for trash can (set by Game after HUD is created)
    this.hud = null;

    this._bind();
  }

  // ─────────────────────────────────────────────────────────
  // Setup
  // ─────────────────────────────────────────────────────────
  _bind() {
    this._onDown  = this._handleDown.bind(this);
    this._onMove  = this._handleMove.bind(this);
    this._onUp    = this._handleUp.bind(this);
    this._onKey   = this._handleKey.bind(this);

    this.canvas.addEventListener('pointerdown', this._onDown);
    this.canvas.addEventListener('pointermove', this._onMove);
    this.canvas.addEventListener('pointerup',   this._onUp);
    window.addEventListener('keydown',          this._onKey);
  }

  destroy() {
    this.canvas.removeEventListener('pointerdown', this._onDown);
    this.canvas.removeEventListener('pointermove', this._onMove);
    this.canvas.removeEventListener('pointerup',   this._onUp);
    window.removeEventListener('keydown',          this._onKey);
    this._clearGhost();
  }

  // ─────────────────────────────────────────────────────────
  // Coordinate conversion
  // ─────────────────────────────────────────────────────────
  _screenToWorld(clientX, clientY) {
    const rect   = this.canvas.getBoundingClientRect();
    // Canvas may be CSS-scaled
    const scaleX = this.app.screen.width  / rect.width;
    const scaleY = this.app.screen.height / rect.height;
    const sx = (clientX - rect.left) * scaleX;
    const sy = (clientY - rect.top)  * scaleY;
    // Account for the world container's camera offset
    return {
      x: sx - this.worldContainer.x,
      y: sy - this.worldContainer.y,
    };
  }

  // ─────────────────────────────────────────────────────────
  // PLACEMENT MODE — called by Game/WordSystem
  // ─────────────────────────────────────────────────────────
  startPlacement(config) {
    this._clearGhost();
    this._placingConfig = config;
    this._buildGhost(config);
    this.canvas.style.cursor = 'crosshair';
  }

  cancelPlacement() {
    this._placingConfig = null;
    this._clearGhost();
    this.canvas.style.cursor = '';
    this.onCancel?.();
  }

  get isPlacing() { return !!this._placingConfig; }
  get isDragging() { return !!this._dragging; }

  _buildGhost(config) {
    const { w, h, color, shape, emoji } = config;
    const c = color ?? 0x888888;

    this._ghost = new PIXI.Container();
    this._ghost.zIndex = Z.ui - 1;
    this._ghost.alpha  = 0.65;

    const bg = new PIXI.Graphics();
    if (shape === 'circle') {
      const r = Math.min(w, h) / 2;
      bg.circle(0, 0, r).fill({ color: c });
      bg.circle(0, 0, r).stroke({ color: 0xFFFFFF, width: 3 });
    } else {
      bg.rect(-w/2, -h/2, w, h).fill({ color: c });
      bg.rect(-w/2, -h/2, w, h).stroke({ color: 0xFFFFFF, width: 3 });
    }
    this._ghost.addChild(bg);

    const icon = new PIXI.Text({
      text: emoji ?? '?',
      style: { fontSize: Math.min(w, h) * 0.5 },
    });
    icon.anchor.set(0.5, 0.5);
    this._ghost.addChild(icon);

    // "Click to place" hint below
    const hint = new PIXI.Text({
      text: 'Click to place  •  Esc to cancel',
      style: { fontSize: 14, fill: 0xFFFFFF,
               stroke: { color: 0x000000, width: 4 },
               dropShadow: { color: 0x000000, blur: 4, distance: 2 } },
    });
    hint.anchor.set(0.5, 0);
    hint.y = h / 2 + 6;
    this._ghost.addChild(hint);

    this.worldContainer.addChild(this._ghost);
    this._ghost.x = this._mx;
    this._ghost.y = this._my;
  }

  _clearGhost() {
    if (this._ghost) {
      if (this._ghost.parent) this._ghost.parent.removeChild(this._ghost);
      this._ghost.destroy({ children: true });
      this._ghost = null;
    }
  }

  // ─────────────────────────────────────────────────────────
  // Event handlers
  // ─────────────────────────────────────────────────────────
  _handleMove(e) {
    const wp   = this._screenToWorld(e.clientX, e.clientY);
    this._mx   = wp.x;
    this._my   = wp.y;

    if (this._ghost) {
      this._ghost.x = wp.x;
      this._ghost.y = wp.y;
    }

    if (this._dragging) {
      const ent = this._dragging;
      ent.body.x  = wp.x + this._dragOffX;
      ent.body.y  = wp.y + this._dragOffY;
      ent.body.vx = 0;
      ent.body.vy = 0;
      ent.gfx.x   = ent.body.x;
      ent.gfx.y   = ent.body.y;

      // Highlight trash can when hovering over it
      const overTrash = this.hud?.isOverTrash(e.clientX, e.clientY);
      this.hud?.highlightTrash(overTrash);
      this.canvas.style.cursor = overTrash ? 'copy' : 'grabbing';
    } else {
      const hit = this._hitTest(wp.x, wp.y);
      this.canvas.style.cursor = hit ? 'grab' : (this._placingConfig ? 'crosshair' : '');
    }
  }

  _handleDown(e) {
    // Ignore if word input is open (Tab box)
    if (e.target !== this.canvas) return;

    const wp = this._screenToWorld(e.clientX, e.clientY);

    // ── Right-click / middle-click cancels placement ──
    if (e.button !== 0) {
      if (this._placingConfig) this.cancelPlacement();
      return;
    }

    // ── PLACEMENT: left-click places the ghost ──
    if (this._placingConfig) {
      const config = this._placingConfig;
      this._placingConfig = null;
      this._clearGhost();
      this.canvas.style.cursor = '';
      this.onPlace?.(config, wp.x, wp.y);
      return;
    }

    // ── DRAG: pick up existing object ──
    const hit = this._hitTest(wp.x, wp.y);
    if (hit) {
      this._dragging    = hit;
      this._dragOffX    = hit.body.x - wp.x;
      this._dragOffY    = hit.body.y - wp.y;
      this._prevStatic  = hit.body.isStatic;
      hit.body.isStatic = true;
      hit.body.vx = 0;
      hit.body.vy = 0;
      hit.body.sleeping = false;
      hit.gfx.alpha = 0.75;
      this.canvas.style.cursor = 'grabbing';
      // Show trash can while dragging
      this.hud?.showTrash(true);
      this.hud?.highlightTrash(false);
      e.preventDefault();
    }
  }

  _handleUp(e) {
    if (this._dragging) {
      const ent = this._dragging;
      this._dragging = null;
      this.canvas.style.cursor = '';
      this.hud?.showTrash(false);
      this.hud?.highlightTrash(false);

      // ── Dropped on trash can → delete ──
      if (this.hud?.isOverTrash(e.clientX, e.clientY)) {
        this.onDelete?.(ent);
        return;
      }

      // ── Dropped in world → restore physics ──
      ent.body.isStatic = this._prevStatic;
      ent.body.sleeping = false;
      ent.body.vx = 0;
      ent.body.vy = 5;
      ent.gfx.alpha = 1;
    }
  }

  _handleKey(e) {
    if (e.key === 'Escape' && this._placingConfig) {
      this.cancelPlacement();
    }
  }

  // ─────────────────────────────────────────────────────────
  // Hit test — returns the topmost entity under (wx, wy)
  // ─────────────────────────────────────────────────────────
  _hitTest(wx, wy) {
    // Check in reverse order (last spawned = on top visually)
    for (let i = this.entities.length - 1; i >= 0; i--) {
      const ent = this.entities[i];
      if (!ent.alive) continue;
      const b = ent.body;
      const hw = b.width / 2 + 8;   // slightly generous hitbox
      const hh = b.height / 2 + 8;
      if (wx >= b.x - hw && wx <= b.x + hw &&
          wy >= b.y - hh && wy <= b.y + hh) {
        return ent;
      }
    }
    return null;
  }
}
